import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";

export const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 28, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskedLines({ lines, className = "" }) {
  return (
    <h1 className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
          <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.12 + i * 0.13, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

export function Kicker({ children, light = false }) {
  return (
    <p className={`flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] ${light ? "text-cream/60" : "text-brand"}`}>
      <span className={`h-px w-8 ${light ? "bg-cream/40" : "bg-brand"}`} />
      {children}
    </p>
  );
}

export function PageHero({ kicker, title, subtitle }) {
  return (
    <header className="border-b border-line">
      <div className="mx-auto max-w-6xl px-6 pb-14 pt-28 md:pt-36">
        <Reveal><Kicker>{kicker}</Kicker></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            {title}
          </h1>
        </Reveal>
        {subtitle && (
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-mute md:text-lg">{subtitle}</p>
          </Reveal>
        )}
      </div>
    </header>
  );
}

export function Section({ title, children, className = "" }) {
  return (
    <section className={className}>
      <h2 className="font-serif text-2xl font-semibold tracking-tight text-ink md:text-3xl">{title}</h2>
      <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-mute md:text-base">{children}</div>
    </section>
  );
}

export function Rich({ text, className = "" }) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className={className}>
      {parts.map((p, i) =>
        p.startsWith("**") ? (
          <strong key={i} className="font-semibold text-ink">{p.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </p>
  );
}

export function UL({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-brand" />
          <Rich text={it} />
        </li>
      ))}
    </ul>
  );
}

export function Callout({ children, title }) {
  return (
    <div className="flex gap-4 border border-line bg-brand-soft p-6" data-testid="callout">
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand" strokeWidth={1.8} />
      <div className="text-[15px] leading-relaxed text-ink">
        {title && <p className="mb-1 font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
}

export function BtnPrimary({ to, children, testid, href }) {
  const cls =
    "group inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-dark";
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.2} />
    </>
  );
  if (href) return <a href={href} data-testid={testid} className={cls}>{inner}</a>;
  return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
}

export function BtnGhost({ to, children, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full border border-ink/25 bg-transparent px-7 py-3.5 text-sm font-bold text-ink transition-colors duration-300 hover:border-brand hover:text-brand"
    >
      {children}
    </Link>
  );
}

export function Marquee({ items, dark = false }) {
  const row = (
    <>
      {items.map((it, i) => (
        <span key={i} className="mx-6 flex items-center gap-6 whitespace-nowrap">
          <span className="font-serif text-lg italic md:text-xl">{it}</span>
          <span className={`h-1.5 w-1.5 rotate-45 ${dark ? "bg-brand" : "bg-brand"}`} />
        </span>
      ))}
    </>
  );
  return (
    <div
      data-testid="brand-marquee"
      className={`overflow-hidden border-y py-5 ${dark ? "border-cream/10 bg-ink text-cream" : "border-line bg-creamalt text-ink"}`}
    >
      <div className="animate-mm-marquee flex w-max">
        <div className="flex">{row}</div>
        <div className="flex" aria-hidden="true">{row}</div>
      </div>
    </div>
  );
}

export function LegalText({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) {
      blocks.push({ ul: list });
      list = null;
    }
  };
  for (const raw of lines) {
    const t = raw.trim();
    if (!t) {
      flush();
      continue;
    }
    if (/^\d+\.\d+[A-Z]?\s+\S/.test(t)) {
      flush();
      blocks.push({ h3: t });
      continue;
    }
    if (/^\d+\.\s+[A-Z]/.test(t)) {
      flush();
      blocks.push({ h2: t });
      continue;
    }
    if (/^[-•]\s+/.test(t) || /^\([a-z]\)\s+/.test(t)) {
      list = list || [];
      list.push(t.replace(/^[-•]\s+/, ""));
      continue;
    }
    if (/^Effective from/i.test(t)) {
      flush();
      blocks.push({ meta: t });
      continue;
    }
    flush();
    blocks.push({ p: t });
  }
  flush();
  return (
    <div className="space-y-4" data-testid="legal-text">
      {blocks.map((b, i) => {
        if (b.h2)
          return (
            <h2 key={i} className="pt-10 font-serif text-2xl font-semibold tracking-tight text-ink md:text-3xl">
              {b.h2}
            </h2>
          );
        if (b.h3)
          return (
            <h3 key={i} className="pt-4 text-base font-bold text-ink">
              {b.h3}
            </h3>
          );
        if (b.meta)
          return (
            <p key={i} className="font-mono text-xs uppercase tracking-[0.18em] text-mute">
              {b.meta}
            </p>
          );
        if (b.ul) return <UL key={i} items={b.ul} />;
        return <Rich key={i} text={b.p} className="text-[15px] leading-relaxed text-mute md:text-base" />;
      })}
    </div>
  );
}

export function LegalDoc({ doc }) {
  return (
    <div className="mx-auto max-w-3xl px-6 py-16 md:py-20">
      {doc.intro?.map((p, i) => (
        <Reveal key={i}><Rich text={p} className="mb-5 text-[15px] leading-relaxed text-mute md:text-base" /></Reveal>
      ))}
      <div className="mt-4 space-y-12">
        {doc.sections.map((s, i) => (
          <Reveal key={i}>
            <Section title={s.title}>
              {s.blocks.map((b, j) => {
                if (b.ul) return <UL key={j} items={b.ul} />;
                if (b.callout) return <Callout key={j}><Rich text={b.callout} /></Callout>;
                if (b.lines) return (
                  <div key={j} className="space-y-1">
                    {b.lines.map((l, k) => <Rich key={k} text={l} />)}
                  </div>
                );
                return <Rich key={j} text={b.p} />;
              })}
            </Section>
          </Reveal>
        ))}
      </div>
      {doc.closing && <Reveal className="mt-12"><Callout><Rich text={doc.closing} /></Callout></Reveal>}
    </div>
  );
}
